import { partnershipKey } from '../utils/partnershipKey';

export { partnershipKey } from '../utils/partnershipKey';

export const generateAllPartnerships = (players, genderMap = {}, mixedPairsFlag = false) => {
  const partnerships = [];
  for (let i = 0; i < players.length; i++) {
    for (let j = i + 1; j < players.length; j++) {
      const g1 = genderMap?.[players[i]];
      const g2 = genderMap?.[players[j]];
      if (mixedPairsFlag && g1 && g2 && g1 === g2) continue;
      partnerships.push([players[i], players[j]]);
    }
  }
  return partnerships;
};

export const partnershipsEqual = (p1, p2) => {
  return (p1[0] === p2[0] && p1[1] === p2[1]) ||
    (p1[0] === p2[1] && p1[1] === p2[0]);
};

export const partnershipExists = (partnership, usedSet) => {
  return usedSet.some((used) => partnershipsEqual(partnership, used));
};

export const findOptimalPartnershipCombination = (availablePartnerships, benchCount, requiredPartnerships) => {
  const sortedPartnerships = [...availablePartnerships].sort((a, b) => {
    const priorityA = (benchCount[a[0]] || 0) + (benchCount[a[1]] || 0);
    const priorityB = (benchCount[b[0]] || 0) + (benchCount[b[1]] || 0);
    return priorityB - priorityA;
  });

  function backtrack(currentPartnerships, usedPlayers, startIndex) {
    if (currentPartnerships.length === requiredPartnerships) {
      return [...currentPartnerships];
    }

    const remainingSlots = requiredPartnerships - currentPartnerships.length;
    const remainingPartnerships = sortedPartnerships.length - startIndex;
    if (remainingPartnerships < remainingSlots) {
      return null;
    }

    for (let i = startIndex; i < sortedPartnerships.length; i++) {
      const partnership = sortedPartnerships[i];
      const [player1, player2] = partnership;

      if (!usedPlayers.has(player1) && !usedPlayers.has(player2)) {
        currentPartnerships.push(partnership);
        usedPlayers.add(player1);
        usedPlayers.add(player2);

        const result = backtrack(currentPartnerships, usedPlayers, i + 1);
        if (result !== null) {
          return result;
        }

        currentPartnerships.pop();
        usedPlayers.delete(player1);
        usedPlayers.delete(player2);
      }
    }

    return null;
  }

  const result = backtrack([], new Set(), 0);
  return result || [];
};

export const findPrioritizedPartnershipCombination = (prioritizedPartnerships, requiredPartnerships) => {
  let bestPartial = [];

  function backtrack(currentPartnerships, usedPlayers, startIndex) {
    if (currentPartnerships.length > bestPartial.length) {
      bestPartial = [...currentPartnerships];
    }

    if (currentPartnerships.length === requiredPartnerships) {
      return [...currentPartnerships];
    }

    const remainingSlots = requiredPartnerships - currentPartnerships.length;
    const remainingPartnerships = prioritizedPartnerships.length - startIndex;
    if (remainingPartnerships < remainingSlots) {
      return null;
    }

    for (let i = startIndex; i < prioritizedPartnerships.length; i++) {
      const partnership = prioritizedPartnerships[i];
      const [player1, player2] = partnership;

      if (!usedPlayers.has(player1) && !usedPlayers.has(player2)) {
        currentPartnerships.push(partnership);
        usedPlayers.add(player1);
        usedPlayers.add(player2);

        const result = backtrack(currentPartnerships, usedPlayers, i + 1);
        if (result !== null) {
          return result;
        }

        currentPartnerships.pop();
        usedPlayers.delete(player1);
        usedPlayers.delete(player2);
      }
    }

    return null;
  }

  const result = backtrack([], new Set(), 0);
  return result || bestPartial;
};

const sortRoundRobinCandidates = (
  partnerships,
  usedPartnerships,
  lastRoundPartnerships,
  recentPartnerships,
  benchCount,
  pairPlayCounts = {}
) => {
  const inLastRound = (pair) => lastRoundPartnerships.some((lp) => partnershipsEqual(lp, pair));
  const inRecentRounds = (pair) => recentPartnerships.some((rp) => partnershipsEqual(rp, pair));
  const pairCount = (pair) => pairPlayCounts[partnershipKey(pair)] || 0;

  return [...partnerships].sort((a, b) => {
    const aUsed = partnershipExists(a, usedPartnerships) ? 1 : 0;
    const bUsed = partnershipExists(b, usedPartnerships) ? 1 : 0;
    if (aUsed !== bUsed) return aUsed - bUsed;

    const aLast = inLastRound(a) ? 1 : 0;
    const bLast = inLastRound(b) ? 1 : 0;
    if (aLast !== bLast) return aLast - bLast;

    const aRecent = inRecentRounds(a) ? 1 : 0;
    const bRecent = inRecentRounds(b) ? 1 : 0;
    if (aRecent !== bRecent) return aRecent - bRecent;

    const aCount = pairCount(a);
    const bCount = pairCount(b);
    if (aCount !== bCount) return aCount - bCount;

    const aBench = (benchCount[a[0]] || 0) + (benchCount[a[1]] || 0);
    const bBench = (benchCount[b[0]] || 0) + (benchCount[b[1]] || 0);
    if (bBench !== aBench) return bBench - aBench;

    const aKey = partnershipKey(a);
    const bKey = partnershipKey(b);
    return aKey.localeCompare(bKey);
  });
};

export const selectRoundRobinPartnerships = ({
  candidatePartnerships,
  requiredPartnerships,
  usedPartnerships = [],
  lastRoundPartnerships = [],
  recentPartnerships = [],
  benchCount = {},
  pairPlayCounts = {}
}) => {
  const inLastRound = (pair) => lastRoundPartnerships.some((lp) => partnershipsEqual(lp, pair));
  const inRecentRounds = (pair) => recentPartnerships.some((rp) => partnershipsEqual(rp, pair));

  const neverUsedCandidates = candidatePartnerships.filter(
    (p) => !partnershipExists(p, usedPartnerships)
  );
  const recentCoveredByUsed = recentPartnerships.every((pair) => partnershipExists(pair, usedPartnerships));
  if (recentCoveredByUsed && neverUsedCandidates.length >= requiredPartnerships) {
    const neverUsedSorted = sortRoundRobinCandidates(
      neverUsedCandidates,
      usedPartnerships,
      lastRoundPartnerships,
      recentPartnerships,
      benchCount,
      pairPlayCounts
    );
    const neverUsedResult = findPrioritizedPartnershipCombination(neverUsedSorted, requiredPartnerships);
    if (neverUsedResult.length === requiredPartnerships) {
      return neverUsedResult;
    }
  }

  const strictNoRecentCandidates = candidatePartnerships.filter(
    (p) => !inLastRound(p) && !inRecentRounds(p)
  );
  if (strictNoRecentCandidates.length >= requiredPartnerships) {
    const strictNoRecentSorted = sortRoundRobinCandidates(
      strictNoRecentCandidates,
      usedPartnerships,
      lastRoundPartnerships,
      recentPartnerships,
      benchCount,
      pairPlayCounts
    );
    const strictNoRecentResult = findPrioritizedPartnershipCombination(strictNoRecentSorted, requiredPartnerships);
    if (strictNoRecentResult.length === requiredPartnerships) {
      return strictNoRecentResult;
    }
  }

  const strictNoLastCandidates = candidatePartnerships.filter((p) => !inLastRound(p));
  if (strictNoLastCandidates.length >= requiredPartnerships) {
    const strictSortedCandidates = sortRoundRobinCandidates(
      strictNoLastCandidates,
      usedPartnerships,
      lastRoundPartnerships,
      recentPartnerships,
      benchCount,
      pairPlayCounts
    );
    const strictResult = findPrioritizedPartnershipCombination(strictSortedCandidates, requiredPartnerships);
    if (strictResult.length === requiredPartnerships) {
      return strictResult;
    }
  }

  const baseAvailable = candidatePartnerships.filter((p) => !partnershipExists(p, usedPartnerships));
  let available = baseAvailable.filter((p) => !inLastRound(p));

  if (available.length < requiredPartnerships) {
    const unseenEvenIfLast = baseAvailable.filter((p) => !available.some((a) => partnershipsEqual(a, p)));
    available = [...available, ...unseenEvenIfLast];
  }

  if (available.length < requiredPartnerships) {
    const seenNotLast = candidatePartnerships.filter(
      (p) => partnershipExists(p, usedPartnerships) && !inLastRound(p)
    );
    for (const pair of seenNotLast) {
      if (available.length >= requiredPartnerships) break;
      if (!available.some((a) => partnershipsEqual(a, pair))) available.push(pair);
    }
  }

  if (available.length < requiredPartnerships) {
    const remaining = candidatePartnerships.filter((p) => !available.some((a) => partnershipsEqual(a, p)));
    for (const pair of remaining) {
      if (available.length >= requiredPartnerships) break;
      available.push(pair);
    }
  }

  const sortedAvailable = sortRoundRobinCandidates(
    available,
    usedPartnerships,
    lastRoundPartnerships,
    recentPartnerships,
    benchCount,
    pairPlayCounts
  );
  let roundPartnerships = findPrioritizedPartnershipCombination(sortedAvailable, requiredPartnerships);

  if (roundPartnerships.length < requiredPartnerships) {
    const sortedAllCandidates = sortRoundRobinCandidates(
      candidatePartnerships,
      usedPartnerships,
      lastRoundPartnerships,
      recentPartnerships,
      benchCount,
      pairPlayCounts
    );
    roundPartnerships = findPrioritizedPartnershipCombination(sortedAllCandidates, requiredPartnerships);
  }

  return roundPartnerships;
};
